<template>
  <Container v-bind="containerProps">
    <div class="w-full h-full relative">
      <select
        ref="select"
        v-model="currentValue"
        :multiple="multiple"
        class="appearance-none h-full w-full text-sm px-2 rounded-md block placeholder:text-gray-300 dark:placeholder:text-gray-600 pr-6 bg-transparent"
        :class="{ 'pointer-events-none' : disabled }"
        @change="$emit('change')"
      >
        <template v-for="(option, index) in normalizedOptionsWithPlaceholder" :key="`${option.value}-optgroup-${index}`">
          <optgroup
            v-if="option.children"

            :value="option.value"
            :label="option.text"
            class="dark:bg-gray-900"
          >
            <option
              v-for="(childOption, index2) in option.children"
              :key="`${childOption.value}-${index}-${index2}`"
              :value="childOption.value"
              class="dark:bg-gray-900"
              v-text="childOption.text"
            />
          </optgroup>
          <option
            v-else
            :value="option.value"
            class="dark:bg-gray-900"
            v-text="option.text"
          />
        </template>
      </select>
      <div class="absolute inset-y-0 pointer-events-none right-0 flex items-center px-2 text-gray-600">
        <o-icon icon="chevronDown" :size="12" />
      </div>
    </div>

    <template #leading>
      <slot name="leading" />
    </template>

    <template #trailing>
      <slot name="trailing" />
    </template>
  </Container>
</template>

<script setup lang="ts">
import flatten from 'lodash/flatten'
import get from 'lodash/get'

import Container from './Container.vue'

interface Props {
  modelValue?: string | number | boolean | Record<string, any> | null
  multiple?: boolean
  // multi mixin props
  valueAttribute?: string
  textAttribute?: string
  options?: any[] | Record<string, any>
  // input mixin props
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  multiple: false,
  valueAttribute: undefined,
  textAttribute: undefined,
  options: () => [],
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'h-9',
  largeSizeClass: 'px-4 text-lg h-12',
  smallSizeClass: 'px-2 text-sm h-8',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value?: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const select = ref<HTMLSelectElement>()
const valueWhenFocus = ref<any>(null)

const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    emit('update:modelValue', value)
  },
})

// -- multi mixin --
function guessOptionValue (option: any) {
  if (props.valueAttribute) return get(option, props.valueAttribute)
  return get(option, 'value', get(option, 'id', get(option, 'text')))
}

function guessOptionText (option: any) {
  if (props.textAttribute) return get(option, props.textAttribute)
  return get(option, 'text', get(option, 'label'))
}

function normalizeOption (option: any): any {
  if (['string', 'number', 'boolean'].includes(typeof option)) {
    return { value: option, text: option }
  }
  if (option.children && Array.isArray(option.children)) {
    return {
      value: guessOptionValue(option),
      text: guessOptionText(option),
      children: option.children.map((childOption: any) => normalizeOption(childOption)),
    }
  }
  return {
    value: guessOptionValue(option),
    text: guessOptionText(option),
  }
}

const normalizedOptions = computed(() => {
  if (props.options) {
    if (Array.isArray(props.options)) {
      return props.options.map((option: any) => normalizeOption(option))
    } else {
      return Object.keys(props.options).map(key => ({
        value: key,
        text: (props.options as Record<string, any>)[key],
      }))
    }
  }
  return []
})

const normalizedOptionsWithPlaceholder = computed(() => {
  if (!props.placeholder) {
    return normalizedOptions.value
  }
  const opts = [...normalizedOptions.value]
  opts.unshift({
    value: null,
    text: props.placeholder,
  })
  return opts
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

// -- input mixin --
const attributes = computed(() => ({
  id: props.id,
  name: props.name,
  required: props.required,
  readonly: props.readonly,
  clear: props.clear,
  disabled: props.disabled,
  size: props.size,
  pattern: props.pattern,
  placeholder: props.placeholder,
}))

const handlers = computed(() => ({
  blur: onBlur,
  focus: onFocus,
  keyup: onKeyUp,
  keydown: onKeyDown,
}))

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

function onBlur (e: Event) {
  emit('blur', e)
  if (currentValue.value !== valueWhenFocus.value) {
    emit('change', currentValue.value)
  }
}

function onFocus (e: Event) {
  emit('focus', e)
  valueWhenFocus.value = currentValue.value
}

function onKeyUp (e: Event) {
  emit('keyup', e)
}

function onKeyDown (e: Event) {
  emit('keydown', e)
}
</script>
